import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  FileText, 
  Loader2,
  User,
  UsersRound,
  FileDown,
  X
} from "lucide-react";
import { adminDashboardApi } from "../../../supports/adminDashboardApi";
import { RecruiterApi } from "../../../supports/recruiterApi";
import ResumeModal from "../../../common/resumeModal";
import { generateRequestId } from "../../../common/common";
import unifiedAuthManager from "../../../services/unifiedAuthManager";

const BroadbeanUsers = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const countPerPage = 10;

    // Resume preview states
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [resumeUrl, setResumeUrl] = useState(null);
    const [resumeFileType, setResumeFileType] = useState(null);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [resumeError, setResumeError] = useState("");
    const [selectedCandidate, setSelectedCandidate] = useState(null);

    const fetchCandidates = useCallback(async (page, search) => {
        setLoading(true);
        try {
            const payload = {
                page: page,
                count: countPerPage,
                search: search || null,
                user_payload: {
                    request_id: generateRequestId(),
                    ...unifiedAuthManager.getUnifiedUser()
                }
            };
            const response = await adminDashboardApi.getBroadbeanCandidates(payload);
            if (response?.data) {
                setCandidates(response.data.candidates || []);
                setTotalCount(response.data.total_count || 0);
            }
        } catch (error) {
            console.error("Error fetching Broadbean candidates:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchCandidates(currentPage, searchTerm);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [currentPage, searchTerm, fetchCandidates]);

    const handleOpenResume = useCallback(async (e, candidate) => {
        if (e) e.stopPropagation();
        setSelectedCandidate(candidate);
        setResumeError("");
        setResumeLoading(true);
        setShowResumeModal(true);
        
        try {
            const response = await RecruiterApi.getResumePreview({
                user_payload: { request_id: generateRequestId() },
                candidate_id: candidate.id,
            });
            
            if (response.success) {
                setResumeUrl(response.data.sas_token_url);
                setResumeFileType(response.data.filetype || null);
            } else {
                setResumeUrl(null);
                setResumeFileType(null);
                setResumeError("Failed to load resume preview.");
            }
        } catch (error) {
            console.error("Error fetching resume preview:", error);
            setResumeError("Failed to load resume. Please try again.");
            setResumeUrl(null);
            setResumeFileType(null);
        } finally {
            setResumeLoading(false);
        }
    }, []);

    const handleCloseResume = () => {
        setShowResumeModal(false);
        setResumeUrl(null);
        setResumeError("");
        setResumeFileType(null);
        setSelectedCandidate(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";
        
        const day = date.getDate().toString().padStart(2, '0');
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        
        return `${day} ${month} ${year}`;
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= Math.ceil(totalCount / countPerPage)) {
            setCurrentPage(newPage);
        }
    };

    const totalPages = Math.ceil(totalCount / countPerPage);

    return (
        <div className="backdrop-blur-sm px-5 py-6">
            <div className="mb-2">
                <h1 className="text-base font-bold text-gray-333">
                    Broadbean Users
                </h1>
                <div className="text-gray-500 text-xs">
                    Candidates applied from Broadbean
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden anim-slideUp mt-4">
                <div className="px-2 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="text-gray-500 bg-gray-100 m-2 rounded-full p-2">
                            <UsersRound size={16} />
                        </div>
                        <span className="text-sm font-semibold text-black">
                            Candidates
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {totalCount}
                        </span>
                    </div>
                    <div className="flex gap-4">
                        <div className="relative">
                            <Search
                                size={16}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type="text"
                                placeholder="Search candidates..."
                                className="pl-10 pr-4 py-2 text-xs text-ellipsis bg-gray-100 rounded-full focus:outline-none focus:ring-0 w-64"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-y-auto h-[calc(100vh-17rem)]">
                    <table className="w-full anim-slideUp">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Candidate Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Phone
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Location
                                </th>
                                
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Job applied for
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Applied On 
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Resume
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-xs text-gray-600">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12">
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                            <p>Loading candidates...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : candidates.length > 0 ? (
                                candidates.map((candidate, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-800">
                                            <div className="flex items-center gap-3">
                                                {/* <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700">
                                                    <User size={16} />
                                                </div> */}
                                                <span className="font-medium text-gray-800">{candidate.full_name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">{candidate.email}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{candidate.phone || "-"}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{candidate.location || "-"}</td>
                                        <td className="px-6 py-4">
                                            {candidate.applied_jobs || "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {candidate.created_at ? formatDate(candidate.created_at) : "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {candidate.resume ? (
                                                <button 
                                                    onClick={(e) => handleOpenResume(e, candidate)}
                                                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                                                    title="Preview Resume"
                                                >
                                                    <svg
                                                        version="1.1"
                                                        id="Icons"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        xmlnsXlink="http://www.w3.org/1999/xlink"
                                                        viewBox="0 0 32 32"
                                                        width="18px"
                                                        fill="#000000"
                                                    >
                                                        <style>{`
                                                            .st0 {
                                                                fill: none;
                                                                stroke: #004645;
                                                                stroke-width: 2;
                                                                stroke-linecap: round;
                                                                stroke-linejoin: round;
                                                                stroke-miterlimit: 10;
                                                            }
                                                        `}</style>
                                                        <polyline
                                                            className="st0"
                                                            points="19,3 19,9 25,9 19,3 7,3 7,29 25,29 25,22.7"
                                                        />
                                                        <circle className="st0" cx="21" cy="19" r="2" />
                                                        <line className="st0" x1="25" y1="9" x2="25" y2="15" />
                                                        <path
                                                            className="st0"
                                                            d="M29,19c0,0-3.6,5-8,5s-8-5-8-5s3.6-5,8-5S29,19,29,19z"
                                                        />
                                                    </svg>
                                                </button>
                                            ) : (
                                                <span className="text-gray-400 italic">No resume</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                                        No candidates found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="flex justify-end gap-4 items-center p-4 border-t">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                            className={`px-1 py-1 text-xs rounded-full border ${
                                currentPage === 1 || loading
                                    ? "opacity-40 cursor-not-allowed"
                                    : "hover:bg-gray-100"
                            }`}
                        >
                            <ChevronLeft size={14} />
                        </button>

                        <div className="text-xs text-gray-600">
                            Page {currentPage} of {totalPages}
                        </div>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || loading}
                            className={`px-1 py-1 text-xs rounded-full border ${
                                currentPage === totalPages || loading
                                    ? "opacity-40 cursor-not-allowed"
                                    : "hover:bg-gray-100"
                            }`}
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                )}
            </div>
            {showResumeModal && (
                <ResumeModal
                    isOpen={showResumeModal}
                    onClose={handleCloseResume}
                    title={(selectedCandidate?.full_name || "Candidate") + " - Resume"}
                    resumeUrl={resumeUrl}
                    filetype={resumeFileType}
                    loading={resumeLoading}
                    error={resumeError}
                />
            )}
        </div>
    );
};

export default BroadbeanUsers;
